import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Warehouse, Plus, Search, Trash2, X, ChevronDown, ChevronUp, ChevronsUpDown,
  Package, Layers, TrendingUp, TrendingDown, Move,
  Building2, MapPin, Edit2, Check, AlertCircle,
  ArrowRight, Minus, RefreshCw, ClipboardList, History
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../api';
import { formatPrice } from '../utils/format';
import { PriceInput } from '../components/PriceInput';
import { useSociete } from '../context/SocieteContext';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';
import { SidePanel } from '../components/SidePanel';
import StocksInventaireTab from './StocksInventaire';

// ─── Constantes ───────────────────────────────────────────────────────────────

const TYPES_ZONE = ['rayon', 'allée', 'casier', 'étagère', 'sol', 'autre'];
const TYPE_ZONE_COLORS = {
  rayon: { bg: '#EFF6FF', color: '#1D4ED8' },
  allée: { bg: '#F0FDF4', color: '#15803D' },
  casier: { bg: '#FFF7ED', color: '#C2410C' },
  étagère: { bg: '#FAF5FF', color: '#7C3AED' },
  sol: { bg: '#F0F9FF', color: '#0369A1' },
  autre: { bg: '#F9FAFB', color: '#6B7280' },
};

const TYPE_MVT_LABELS = {
  entree: 'Entrée',
  sortie: 'Sortie',
  ajustement: 'Ajustement',
  deplacement: 'Déplacement',
};
const TYPE_MVT_COLORS = {
  entree: '#15803D',
  sortie: '#DC2626',
  ajustement: '#B45309',
  deplacement: '#1D4ED8',
};

// ─── StatCard ─────────────────────────────────────────────────────────────────

function StatCard({ title, value, sub, icon: Icon, color }) {
  return (
    <div className="bg-white rounded-xl p-4 border" style={{ borderColor: 'var(--border)' }}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-2xl font-bold" style={{ color: 'var(--text)',  }}>{value}</p>
          <p className="text-xs mt-0.5 font-medium" style={{ color }}>{title}</p>
          {sub && <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{sub}</p>}
        </div>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: color + '18' }}>
          <Icon size={18} style={{ color }} />
        </div>
      </div>
    </div>
  );
}

// ─── ZoneBadge ────────────────────────────────────────────────────────────────

function ZoneBadge({ type }) {
  const c = TYPE_ZONE_COLORS[type] || TYPE_ZONE_COLORS.autre;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize"
      style={{ background: c.bg, color: c.color }}>{type || 'autre'}</span>
  );
}

// ─── ArticleSearchField ───────────────────────────────────────────────────────

function ArticleSearchField({ value, onChange, articles, placeholder = 'Rechercher un article…' }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = articles.find(a => a.article_id === value);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const filtered = articles.filter(a => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (a.reference || '').toLowerCase().includes(q) || (a.designation || '').toLowerCase().includes(q);
  }).slice(0, 30);

  if (selected) {
    return (
      <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg border"
        style={{ background: 'var(--primary-50)', borderColor: 'var(--primary-light)' }}>
        <span className="font-mono text-xs px-1.5 py-0.5 rounded flex-shrink-0"
          style={{ background: 'var(--primary-100)', color: 'var(--primary-solid)' }}>
          {selected.reference}
        </span>
        <span className="text-xs flex-1 truncate font-medium" style={{ color: 'var(--text)' }}>
          {selected.designation}
        </span>
        <button type="button" onClick={() => onChange('')}
          className="p-0.5 rounded hover:bg-red-50 flex-shrink-0" style={{ color: '#DC2626' }}>
          <X size={11} />
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--text-placeholder)' }} />
      <input value={search} onChange={e => { setSearch(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)} placeholder={placeholder}
        className="w-full pl-7 pr-3 py-2 border rounded-lg text-sm focus:outline-none"
        style={{ borderColor: 'var(--border)' }} />
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg overflow-hidden"
          style={{ borderColor: 'var(--border)' }}>
          {filtered.length === 0
            ? <p className="px-3 py-2 text-xs" style={{ color: 'var(--text-placeholder)' }}>Aucun article</p>
            : <div className="max-h-48 overflow-y-auto">
                {filtered.map(a => (
                  <button key={a.article_id} type="button"
                    onMouseDown={() => { onChange(a.article_id); setSearch(''); setOpen(false); }}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 text-xs"
                    style={{ borderBottom: '1px solid #F2F2EE' }}>
                    <span className="font-mono px-1.5 py-0.5 rounded flex-shrink-0"
                      style={{ background: 'var(--border-light)', color: 'var(--text-secondary)' }}>
                      {a.reference}
                    </span>
                    <span className="flex-1 truncate" style={{ color: 'var(--text)' }}>{a.designation}</span>
                  </button>
                ))}
              </div>}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function Stocks() {
  const { activeSociete, activeAgences } = useSociete();
  const _ctxKey = `${activeSociete?.societe_id || ''}|${activeAgences.map(a => a.agence_id).join(',')}`;

  const [stocks, setStocks] = useState([]);
  const [agences, setAgences] = useState([]);
  const [allArticles, setAllArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterAgence, setFilterAgence] = useState('');

  // Stock side panel
  const [panelOpen, setPanelOpen] = useState(false);
  const [editStock, setEditStock] = useState(null);
  const [activeTab, setActiveTab] = useState('info');
  const [form, setForm] = useState({ nom: '', description: '', adresse: '', agence_id: '', agence_nom: '', societe_id: '', societe_nom: '', odoo_warehouse_id: '' });

  // Vue principale : 'depots' uniquement (l'historique est maintenant sur /mouvements-stock)
  

  // Odoo warehouses (pour le mapping)
  const [odooWarehouses, setOdooWarehouses] = useState([]);
  const [loadingWH, setLoadingWH] = useState(false);

  // Zones (loaded when stock selected)
  const [zones, setZones] = useState([]);
  const [zoneForm, setZoneForm] = useState({ nom: '', type_zone: 'rayon', ordre: null, is_defaut: false, is_douane: false });
  const [addingZone, setAddingZone] = useState(false);
  const [editZone, setEditZone] = useState(null); // zone_id being edited

  // Articles in stock
  const [stockArticles, setStockArticles] = useState([]);
  const [filterZone, setFilterZone] = useState('');
  const [mouvements, setMouvements] = useState([]);
  const [showMouvements, setShowMouvements] = useState(false);

  // Ajustement form
  const [ajustOpen, setAjustOpen] = useState(false);
  const [ajustForm, setAjustForm] = useState({ article_id: '', zone_id: '', quantite: '', type_mouvement: 'entree', prix_unitaire: '', numero_serie: '', commentaire: '' });

  // Déplacement form
  const [deplOpen, setDeplOpen] = useState(false);
  const [deplForm, setDeplForm] = useState({ article_id: '', zone_source_id: '', stock_dest_id: '', zone_dest_id: '', quantite: '', mode: 'article', commentaire: '' });
  const [destZones, setDestZones] = useState([]);

  const [saving, setSaving] = useState(false);

  // ── Load ─────────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [stocksRes, agencesRes, articlesRes] = await Promise.all([
        api.getStocks(),
        api.getAgences(),
        api.getArticles({ actif: true }),
      ]);
      setStocks(Array.isArray(stocksRes.data) ? stocksRes.data : []);
      setAgences(Array.isArray(agencesRes.data) ? agencesRes.data : []);
      const arts = Array.isArray(articlesRes.data) ? articlesRes.data : [];
      setAllArticles(arts.filter(a => ['piece_detachee', 'materiel'].includes(a.type)));
    } catch { toast.error('Erreur lors du chargement'); }
    finally { setLoading(false); }
  }, [_ctxKey]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const loadStockDetail = useCallback(async (stockId) => {
    if (!stockId) return;
    try {
      const [detailRes, articlesRes, mvtRes] = await Promise.all([
        api.getStock(stockId),
        api.getStockArticles(stockId),
        api.getStockMouvements(stockId, 50),
      ]);
      setZones(detailRes.data.zones || []);
      setStockArticles(Array.isArray(articlesRes.data) ? articlesRes.data : []);
      setMouvements(Array.isArray(mvtRes.data) ? mvtRes.data : []);
    } catch { toast.error('Erreur chargement stock'); }
  }, []);

  // ── Dest zones for déplacement ────────────────────────────────────────────

  useEffect(() => {
    if (!deplForm.stock_dest_id) { setDestZones([]); return; }
    api.getStock(deplForm.stock_dest_id)
      .then(r => setDestZones(r.data.zones || []))
      .catch(() => setDestZones([]));
  }, [deplForm.stock_dest_id]);

  // ── Filtered + sorted stocks ──────────────────────────────────────────────

  const filtered = stocks.filter(s => {
    if (filterAgence && s.agence_id !== filterAgence) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (s.nom || '').toLowerCase().includes(q) ||
           (s.agence_nom || '').toLowerCase().includes(q) ||
           (s.societe_nom || '').toLowerCase().includes(q);
  });

  const [sortConfig, setSortConfig] = useState({ key: 'nom', dir: 'asc' });

  const STOCK_COLS = [
    { key: 'nom',    label: 'Nom',        get: s => s.nom || '' },
    { key: 'agence', label: 'Agence',     get: s => s.agence_nom || '' },
    { key: 'societe',label: 'Société',    get: s => s.societe_nom || '' },
    { key: 'zones',  label: 'Zones',      get: s => s.nb_zones   ?? 0, num: true },
    { key: 'refs',   label: 'Références', get: s => s.nb_refs    ?? 0, num: true },
    { key: 'qty',    label: 'Qté totale', get: s => s.total_qty  ?? 0, num: true },
    { key: 'adresse',label: 'Adresse',    get: s => s.adresse || '' },
  ];

  const sortedFiltered = [...filtered].sort((a, b) => {
    const col = STOCK_COLS.find(c => c.key === sortConfig.key);
    if (!col) return 0;
    const av = col.get(a), bv = col.get(b);
    if (col.num) return sortConfig.dir === 'asc' ? av - bv : bv - av;
    return sortConfig.dir === 'asc'
      ? String(av).localeCompare(String(bv), 'fr')
      : String(bv).localeCompare(String(av), 'fr');
  });

  const toggleSort = (key) => setSortConfig(prev =>
    prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
  );

  // Stats
  const totalZones = stocks.reduce((s, k) => s + (k.nb_zones || 0), 0);
  const totalRefs = stocks.reduce((s, k) => s + (k.nb_refs || 0), 0);
  const totalQty = stocks.reduce((s, k) => s + (k.total_qty || 0), 0);

  // ── Unique agences from stocks ────────────────────────────────────────────

  const agencesInStocks = [...new Map(
    stocks.filter(s => s.agence_id && s.agence_nom)
      .map(s => [s.agence_id, { agence_id: s.agence_id, nom: s.agence_nom }])
  ).values()].sort((a, b) => a.nom.localeCompare(b.nom));

  // ── Panel open/close ──────────────────────────────────────────────────────

  const _loadOdooWarehouses = async () => {
    if (odooWarehouses.length > 0) return; // déjà chargés
    setLoadingWH(true);
    try {
      const r = await api.getOdooWarehouses();
      setOdooWarehouses(Array.isArray(r.data) ? r.data : []);
    } catch { /* silencieux si Odoo inaccessible */ }
    finally { setLoadingWH(false); }
  };

  const openNew = () => {
    setEditStock(null);
    const defAgence = agences.length === 1 ? agences[0] : null;
    setForm({ nom: '', description: '', adresse: '',
      agence_id: defAgence?.agence_id || '', agence_nom: defAgence?.nom || '',
      societe_id: '', societe_nom: '', odoo_warehouse_id: '' });
    setZones([]); setStockArticles([]); setMouvements([]);
    setActiveTab('info'); setAjustOpen(false); setDeplOpen(false);
    _loadOdooWarehouses();
    setPanelOpen(true);
  };

  const openEdit = async (s) => {
    setEditStock(s);
    setForm({ nom: s.nom, description: s.description || '', adresse: s.adresse || '',
      agence_id: s.agence_id || '', agence_nom: s.agence_nom || '',
      societe_id: s.societe_id || '', societe_nom: s.societe_nom || '',
      odoo_warehouse_id: s.odoo_warehouse_id || '' });
    setActiveTab('info'); setAjustOpen(false); setDeplOpen(false); setShowMouvements(false);
    setFilterZone('');
    _loadOdooWarehouses();
    setPanelOpen(true);
    await loadStockDetail(s.stock_id);
  };

  // ── Stock CRUD ────────────────────────────────────────────────────────────

  const handleSaveStock = async () => {
    if (!form.nom.trim()) { toast.error('Le nom est obligatoire'); return; }
    setSaving(true);
    try {
      if (editStock) {
        await api.updateStock(editStock.stock_id, form);
        toast.success('Stock mis à jour');
      } else {
        await api.createStock(form);
        toast.success('Stock créé');
      }
      setPanelOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleDeleteStock = async () => {
    if (!editStock) return;
    if (!window.confirm('Supprimer ce stock ?')) return;
    try {
      await api.deleteStock(editStock.stock_id);
      toast.success('Stock supprimé');
      setPanelOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  // ── Zone CRUD ─────────────────────────────────────────────────────────────

  const handleAddZone = async () => {
    if (!zoneForm.nom.trim()) { toast.error('Nom de zone obligatoire'); return; }
    try {
      await api.createZone(editStock.stock_id, zoneForm);
      setZoneForm({ nom: '', type_zone: 'rayon', ordre: null, is_defaut: false, is_douane: false });
      setAddingZone(false);
      await loadStockDetail(editStock.stock_id);
      toast.success('Zone créée');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleSaveZone = async (zoneId) => {
    const z = zones.find(z => z.zone_id === zoneId);
    if (!z || !z._editNom?.trim()) { toast.error('Nom obligatoire'); return; }
    try {
      await api.updateZone(editStock.stock_id, zoneId, { nom: z._editNom, type_zone: z._editType, is_defaut: !!z._editDefaut, is_douane: !!z._editDouane });
      setEditZone(null);
      await loadStockDetail(editStock.stock_id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDeleteZone = async (zoneId) => {
    if (!window.confirm('Supprimer cette zone ?')) return;
    try {
      await api.deleteZone(editStock.stock_id, zoneId);
      await loadStockDetail(editStock.stock_id);
      toast.success('Zone supprimée');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const startEditZone = (z) => {
    setZones(prev => prev.map(zz => zz.zone_id === z.zone_id
      ? { ...zz, _editNom: zz.nom, _editType: zz.type_zone, _editDefaut: zz.is_defaut, _editDouane: zz.is_douane }
      : zz
    ));
    setEditZone(z.zone_id);
  };

  // ── Ajustement ────────────────────────────────────────────────────────────

  const handleAjuster = async () => {
    if (!ajustForm.article_id) { toast.error('Sélectionnez un article'); return; }
    const selectedArt = allArticles.find(a => a.article_id === ajustForm.article_id);
    const isMateriel = selectedArt?.type === 'materiel';

    if (isMateriel && ajustForm.type_mouvement === 'entree') {
      if (!ajustForm.numero_serie.trim()) { toast.error('Le numéro de série est obligatoire'); return; }
    } else {
      if (!ajustForm.quantite || isNaN(Number(ajustForm.quantite))) { toast.error('Quantité invalide'); return; }
    }
    setSaving(true);
    try {
      const res = await api.ajusterStockArticle(editStock.stock_id, {
        article_id: ajustForm.article_id,
        zone_id: ajustForm.zone_id || null,
        quantite: isMateriel && ajustForm.type_mouvement === 'entree' ? 1 : Number(ajustForm.quantite),
        type_mouvement: ajustForm.type_mouvement,
        prix_unitaire: ajustForm.prix_unitaire ? Number(ajustForm.prix_unitaire) : null,
        numero_serie: ajustForm.numero_serie.trim() || null,
        commentaire: ajustForm.commentaire || null,
      });
      if (res.data.created_materiel) {
        toast.success(`Stock mis à jour · Fiche matériel créée (${res.data.created_materiel.materiel_id})`);
      } else {
        toast.success('Stock mis à jour');
      }
      setAjustOpen(false);
      setAjustForm({ article_id: '', zone_id: '', quantite: '', type_mouvement: 'entree', prix_unitaire: '', numero_serie: '', commentaire: '' });
      await loadStockDetail(editStock.stock_id);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  // ── Déplacement ───────────────────────────────────────────────────────────

  const openDepl = (article = null, zoneId = null) => {
    setDeplForm({
      article_id: article?.article_id || '',
      zone_source_id: zoneId || '',
      stock_dest_id: '',
      zone_dest_id: '',
      quantite: article ? String(article.quantite_disponible || '') : '',
      mode: article ? 'article' : 'zone',
      commentaire: '',
    });
    setDeplOpen(true);
  };

  const handleDeplacer = async () => {
    if (!deplForm.stock_dest_id) { toast.error('Sélectionnez le stock de destination'); return; }
    if (deplForm.mode === 'article' && !deplForm.article_id) { toast.error('Sélectionnez un article'); return; }
    if (deplForm.mode === 'zone' && !deplForm.zone_source_id) { toast.error('Sélectionnez la zone source'); return; }
    setSaving(true);
    try {
      const res = await api.deplacerStock({
        stock_source_id: editStock.stock_id,
        zone_source_id: deplForm.zone_source_id || null,
        stock_dest_id: deplForm.stock_dest_id,
        zone_dest_id: deplForm.zone_dest_id || null,
        article_id: deplForm.mode === 'article' ? deplForm.article_id : null,
        quantite: deplForm.quantite ? Number(deplForm.quantite) : null,
        commentaire: deplForm.commentaire || null,
      });
      const nb = res.data.deplaced || 0;
      toast.success(`${nb} article${nb > 1 ? 's' : ''} déplacé${nb > 1 ? 's' : ''}`);
      setDeplOpen(false);
      await loadStockDetail(editStock.stock_id);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  // ── Helpers ───────────────────────────────────────────────────────────────

  const getZoneName = (zoneId) => zones.find(z => z.zone_id === zoneId)?.nom || '—';
  const getStockName = (stockId) => stocks.find(s => s.stock_id === stockId)?.nom || '—';

  const inp = "w-full px-3 py-2 rounded-lg border text-sm focus:outline-none";
  const inpSt = { borderColor: 'var(--border)' };
  const lbl = "block text-xs font-semibold uppercase tracking-wider mb-1";
  const lblSt = { color: 'var(--text-muted)' };

  // ── Filtered stock articles ───────────────────────────────────────────────

  const filteredArticles = stockArticles.filter(a =>
    !filterZone || a.zone_id === filterZone || (!a.zone_id && filterZone === '_none')
  );

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin w-8 h-8 border-2 rounded-full"
          style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <ReadOnlyBanner />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--primary-solid)',  }}>Stocks</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>Gestion des stocks par agence</p>
        </div>
        <button onClick={openNew} data-testid="add-stock-btn"
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white"
          style={{ background: 'var(--primary-gradient)' }}>
          <Plus size={16} /> Nouveau stock
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard title="Stocks" value={stocks.length} icon={Warehouse} color="var(--primary-solid)" />
        <StatCard title="Zones" value={totalZones} icon={Layers} color="#7C3AED" />
        <StatCard title="Références" value={totalRefs} sub="articles en stock" icon={Package} color="#C2410C" />
        <StatCard title="Quantité totale" value={formatPrice(totalQty, 1)} icon={TrendingUp} color="#15803D" />
      </div>

      {/* Filtres */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative w-72">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--text-placeholder)' }} />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher un stock..." data-testid="search-stocks"
            className="w-full pl-9 pr-4 py-2 rounded-lg border text-sm"
            style={{ borderColor: 'var(--border)', background: 'white' }} />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setFilterAgence('')}
            className="px-3 py-1.5 rounded-full text-xs font-medium border transition-all"
            style={{
              background: !filterAgence ? 'var(--primary-solid)' : 'white',
              color: !filterAgence ? 'white' : 'var(--text-muted)',
              borderColor: !filterAgence ? 'var(--primary-solid)' : 'var(--border)',
            }}>
            Toutes les agences
          </button>
          {agencesInStocks.map(a => (
            <button key={a.agence_id} onClick={() => setFilterAgence(a.agence_id)}
              className="px-3 py-1.5 rounded-full text-xs font-medium border transition-all"
              style={{
                background: filterAgence === a.agence_id ? 'var(--primary-solid)' : 'white',
                color: filterAgence === a.agence_id ? 'white' : 'var(--text-muted)',
                borderColor: filterAgence === a.agence_id ? 'var(--primary-solid)' : 'var(--border)',
              }}>
              {a.nom}
            </button>
          ))}
        </div>
      </div>

      {/* Table stocks */}
      {filtered.length === 0 ? (
        <div className="rounded-xl py-16 text-center" style={{ border: '1px dashed #E6E6E1' }}>
          <Warehouse size={36} className="mx-auto mb-3" style={{ color: 'var(--text-disabled)' }} />
          <p className="text-sm font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
            {search || filterAgence ? 'Aucun résultat' : 'Aucun stock créé'}
          </p>
          {!search && !filterAgence && (
            <p className="text-xs mb-4" style={{ color: 'var(--text-placeholder)' }}>
              Créez votre premier stock avec le bouton "Nouveau stock"
            </p>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid #E6E6E1' }}>
                  {STOCK_COLS.map(col => {
                    const active = sortConfig.key === col.key;
                    return (
                      <th key={col.key}
                        onClick={() => toggleSort(col.key)}
                        className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap cursor-pointer select-none group"
                        style={{ color: active ? 'var(--primary-solid)' : 'var(--text-muted)' }}>
                        <div className="flex items-center gap-1">
                          {col.label}
                          {active
                            ? (sortConfig.dir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)
                            : <ChevronsUpDown size={11} className="opacity-30 group-hover:opacity-60 transition-opacity" />}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sortedFiltered.map(s => (
                  <tr key={s.stock_id} data-testid="stock-row"
                    onClick={() => openEdit(s)}
                    className="hover:bg-blue-50/40 transition-colors cursor-pointer"
                    style={{ borderBottom: '1px solid var(--border-light)' }}>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ background: 'var(--primary-50)' }}>
                          <Warehouse size={13} style={{ color: 'var(--primary-solid)' }} />
                        </div>
                        <span className="font-semibold text-sm" style={{ color: 'var(--text)' }}>{s.nom}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      {s.agence_nom
                        ? <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--text-secondary)' }}>
                            <MapPin size={11} />{s.agence_nom}
                          </span>
                        : <span className="text-xs" style={{ color: 'var(--text-disabled)' }}>—</span>}
                    </td>
                    <td className="px-3 py-2.5">
                      {s.societe_nom
                        ? <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--text-secondary)' }}>
                            <Building2 size={11} />{s.societe_nom}
                          </span>
                        : <span className="text-xs" style={{ color: 'var(--text-disabled)' }}>—</span>}
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="text-xs font-semibold" style={{ color: '#7C3AED' }}>{s.nb_zones || 0}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="text-xs font-semibold" style={{ color: '#C2410C' }}>{s.nb_refs || 0}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="text-xs font-semibold" style={{ color: '#15803D' }}>
                        {formatPrice(s.total_qty || 0, 1)}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="text-xs truncate" style={{ color: 'var(--text-placeholder)', maxWidth: 160, display: 'block' }}>
                        {s.adresse || '—'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2.5 border-t text-xs" style={{ borderColor: 'var(--border)', color: 'var(--text-placeholder)' }}>
            {filtered.length} stock{filtered.length !== 1 ? 's' : ''}
          </div>
        </div>
      )}

      {/* ─── Side Panel ──────────────────────────────────────────────────────── */}
      <SidePanel
        open={panelOpen}
        onOpenChange={o => { if (!o) { setPanelOpen(false); setAjustOpen(false); setDeplOpen(false); } }}
        size="lg"
        title={editStock ? editStock.nom : 'Nouveau stock'}
        subtitle={editStock ? `${editStock.agence_nom || ''} ${editStock.societe_nom ? '· ' + editStock.societe_nom : ''}`.trim() : 'Créer un nouveau stock'}
        footer={
          <div className="flex items-center justify-between">
            {editStock ? (
              <button type="button" onClick={handleDeleteStock}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium"
                style={{ color: '#DC2626' }}>
                <Trash2 size={14} /> Supprimer
              </button>
            ) : <span />}
            <div className="flex gap-3">
              <button type="button" onClick={() => setPanelOpen(false)}
                className="px-4 py-2.5 rounded-lg text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
                Fermer
              </button>
              {(activeTab === 'info') && (
                <button data-testid="save-stock-btn" type="button" onClick={handleSaveStock}
                  disabled={saving}
                  className="px-6 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                  style={{ background: 'var(--primary-gradient)' }}>
                  {saving ? 'Enregistrement...' : (editStock ? 'Mettre à jour' : 'Créer')}
                </button>
              )}
            </div>
          </div>
        }
      >
        {/* Tabs — only visible for existing stocks */}
        {editStock && (
          <div className="flex border-b -mx-6 px-6 mb-5 overflow-x-auto" style={{ borderColor: 'var(--border)' }}>
            {[
              { key: 'info', label: 'Informations', icon: Warehouse },
              { key: 'zones', label: `Zones (${zones.length})`, icon: Layers },
              { key: 'articles', label: `Articles (${stockArticles.filter(a => a.quantite_disponible > 0).length})`, icon: Package },
              { key: 'mouvements', label: 'Mouvements', icon: History },
              { key: 'inventaires', label: 'Inventaires', icon: ClipboardList },
            ].map(t => (
              <button key={t.key} type="button" onClick={() => { setActiveTab(t.key); setAjustOpen(false); setDeplOpen(false); }}
                data-testid={`stock-tab-${t.key}`}
                className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold border-b-2 transition-colors -mb-px"
                style={{
                  borderBottomColor: activeTab === t.key ? 'var(--primary-solid)' : 'transparent',
                  color: activeTab === t.key ? 'var(--primary-solid)' : 'var(--text-muted)',
                  background: 'transparent',
                }}>
                <t.icon size={12} />{t.label}
              </button>
            ))}
          </div>
        )}

        {/* ── Info tab ── */}
        {activeTab === 'info' && (
          <div className="space-y-4">
            <div>
              <label className={lbl} style={lblSt}>Nom du stock <span style={{ color: '#DC2626' }}>*</span></label>
              <input value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))}
                placeholder="Ex: Dépôt principal, Magasin Nord…" className={inp} style={inpSt}
                data-testid="stock-nom-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={lbl} style={lblSt}>Agence</label>
                <select value={form.agence_id}
                  onChange={e => {
                    const ag = agences.find(a => a.agence_id === e.target.value);
                    setForm(f => ({ ...f, agence_id: ag?.agence_id || '', agence_nom: ag?.nom || '' }));
                  }}
                  className={inp} style={inpSt} data-testid="stock-agence-select">
                  <option value="">— Aucune agence —</option>
                  {agences.map(a => (
                    <option key={a.agence_id} value={a.agence_id}>{a.nom}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={lbl} style={lblSt}>Société</label>
                <input value={form.societe_nom}
                  onChange={e => setForm(f => ({ ...f, societe_nom: e.target.value }))}
                  placeholder="Nom de la société" className={inp} style={inpSt} />
              </div>
            </div>
            <div>
              <label className={lbl} style={lblSt}>Adresse</label>
              <input value={form.adresse} onChange={e => setForm(f => ({ ...f, adresse: e.target.value }))}
                placeholder="Adresse physique du stock" className={inp} style={inpSt} />
            </div>
            <div>
              <label className={lbl} style={lblSt}>Description</label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                rows={2} placeholder="Notes, informations complémentaires…"
                className={inp} style={{ ...inpSt, resize: 'none' }} />
            </div>
            <div>
              <label className={lbl} style={lblSt}>
                Entrepôt Odoo
                <span className="ml-1 text-xs font-normal" style={{ color: 'var(--text-placeholder)' }}>(mapping pour synchro articles)</span>
              </label>
              <select value={form.odoo_warehouse_id}
                onChange={e => setForm(f => ({ ...f, odoo_warehouse_id: e.target.value }))}
                className={inp} style={inpSt}
                data-testid="stock-odoo-warehouse-select"
                disabled={loadingWH}>
                <option value="">— Non mappé —</option>
                {odooWarehouses.map(w => (
                  <option key={w.id} value={w.id}>{w.name} ({w.code})</option>
                ))}
              </select>
              {loadingWH && <p className="text-xs mt-1" style={{ color: 'var(--text-placeholder)' }}>Chargement des entrepôts Odoo…</p>}
            </div>
          </div>
        )}

        {/* ── Zones tab ── */}
        {activeTab === 'zones' && editStock && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                Zones de rangement dans ce stock
              </p>
              <button type="button" onClick={() => setAddingZone(true)} data-testid="add-zone-btn"
                className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg"
                style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)', border: '1px solid var(--primary-100)' }}>
                <Plus size={11} /> Ajouter une zone
              </button>
            </div>

            {/* Inline add zone */}
            {addingZone && (
              <div className="rounded-xl p-3 border space-y-3" style={{ borderColor: 'var(--primary-light)', background: 'var(--primary-50)' }}>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className={lbl} style={lblSt}>Nom</label>
                    <input value={zoneForm.nom} onChange={e => setZoneForm(f => ({ ...f, nom: e.target.value }))}
                      placeholder="Ex: Rayon A, Casier 01…" className={inp} style={inpSt} autoFocus />
                  </div>
                  <div>
                    <label className={lbl} style={lblSt}>Type</label>
                    <select value={zoneForm.type_zone} onChange={e => setZoneForm(f => ({ ...f, type_zone: e.target.value }))}
                      className={inp} style={inpSt}>
                      {TYPES_ZONE.map(t => <option key={t} value={t} className="capitalize">{t}</option>)}
                    </select>
                  </div>
                </div>
                <div className="flex gap-4 justify-between items-center flex-wrap">
                  <label className="flex items-center gap-2 cursor-pointer text-xs" style={{ color: 'var(--text-secondary)' }}>
                    <input
                      type="checkbox"
                      checked={!!zoneForm.is_defaut}
                      onChange={e => setZoneForm(f => ({ ...f, is_defaut: e.target.checked }))}
                      className="rounded"
                    />
                    Zone par défaut (articles en transit / sans zone affectée)
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-xs" style={{ color: '#92400E' }}>
                    <input
                      type="checkbox"
                      checked={!!zoneForm.is_douane}
                      onChange={e => setZoneForm(f => ({ ...f, is_douane: e.target.checked }))}
                      className="rounded"
                    />
                    Zone sous douane
                  </label>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => { setAddingZone(false); setZoneForm({ nom: '', type_zone: 'rayon', ordre: null, is_defaut: false }); }}
                      className="px-3 py-1.5 rounded-lg text-xs" style={{ color: 'var(--text-muted)' }}>Annuler</button>
                    <button type="button" onClick={handleAddZone}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
                      style={{ background: 'var(--primary-gradient)' }}>
                      <Check size={11} /> Créer
                    </button>
                  </div>
                </div>
              </div>
            )}

            {zones.length === 0 && !addingZone && (
              <div className="rounded-xl py-10 text-center" style={{ border: '1px dashed var(--border)' }}>
                <Layers size={28} className="mx-auto mb-2" style={{ color: 'var(--text-disabled)' }} />
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Aucune zone — ajoutez des zones de rangement</p>
              </div>
            )}

            <div className="space-y-2">
              {zones.map(z => (
                <div key={z.zone_id} data-testid="zone-row"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl border bg-white"
                  style={{ borderColor: 'var(--border)' }}>
                  {editZone === z.zone_id ? (
                    <>
                      <input value={z._editNom || ''}
                        onChange={e => setZones(prev => prev.map(zz => zz.zone_id === z.zone_id ? { ...zz, _editNom: e.target.value } : zz))}
                        className="flex-1 px-2 py-1 border rounded-md text-sm focus:outline-none"
                        style={{ borderColor: 'var(--border)' }} autoFocus />
                      <select value={z._editType || z.type_zone || 'autre'}
                        onChange={e => setZones(prev => prev.map(zz => zz.zone_id === z.zone_id ? { ...zz, _editType: e.target.value } : zz))}
                        className="px-2 py-1 border rounded-md text-xs focus:outline-none"
                        style={{ borderColor: 'var(--border)' }}>
                        {TYPES_ZONE.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <label className="flex items-center gap-1 text-xs cursor-pointer" style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                        <input type="checkbox" checked={!!z._editDefaut}
                          onChange={e => setZones(prev => prev.map(zz => zz.zone_id === z.zone_id ? { ...zz, _editDefaut: e.target.checked } : zz))}
                          className="rounded" />
                        Par défaut
                      </label>
                      <label className="flex items-center gap-1 text-xs cursor-pointer" style={{ color: '#92400E', whiteSpace: 'nowrap' }}>
                        <input type="checkbox" checked={!!z._editDouane}
                          onChange={e => setZones(prev => prev.map(zz => zz.zone_id === z.zone_id ? { ...zz, _editDouane: e.target.checked } : zz))}
                          className="rounded" />
                        Douane
                      </label>
                      <button type="button" onClick={() => handleSaveZone(z.zone_id)}
                        className="p-1.5 rounded-lg" style={{ background: '#F0FDF4', color: '#15803D' }}>
                        <Check size={13} />
                      </button>
                      <button type="button" onClick={() => setEditZone(null)}
                        className="p-1.5 rounded-lg hover:bg-gray-100" style={{ color: 'var(--text-muted)' }}>
                        <X size={13} />
                      </button>
                    </>
                  ) : (
                    <>
                      <Layers size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                      <span className="flex-1 text-sm font-medium" style={{ color: 'var(--text)' }}>{z.nom}</span>
                      <ZoneBadge type={z.type_zone} />
                      {z.is_defaut && (
                        <span style={{ fontSize: 10, background: '#FEF3C7', color: '#92400E', border: '1px solid #FDE68A', borderRadius: 4, padding: '1px 6px', fontWeight: 600 }}>
                          Par défaut
                        </span>
                      )}
                      {z.is_douane && (
                        <span style={{ fontSize: 10, background: '#FFF7ED', color: '#C2410C', border: '1px solid #FDBA74', borderRadius: 4, padding: '1px 6px', fontWeight: 600 }}>
                          Douane
                        </span>
                      )}
                      <button type="button" onClick={() => startEditZone(z)}
                        className="p-1.5 rounded-lg hover:bg-gray-100" style={{ color: 'var(--text-muted)' }}>
                        <Edit2 size={12} />
                      </button>
                      <button type="button" onClick={() => handleDeleteZone(z.zone_id)}
                        className="p-1.5 rounded-lg hover:bg-red-50" style={{ color: '#DC2626' }}>
                        <Trash2 size={12} />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Articles tab ── */}
        {activeTab === 'articles' && editStock && (
          <div className="space-y-3">
            {/* Toolbar */}
            <div className="flex items-center gap-2 flex-wrap">
              <select value={filterZone} onChange={e => setFilterZone(e.target.value)}
                className="px-2.5 py-1.5 border rounded-lg text-xs focus:outline-none"
                style={{ borderColor: 'var(--border)' }}>
                <option value="">Toutes les zones</option>
                <option value="_none">Sans zone</option>
                {zones.map(z => <option key={z.zone_id} value={z.zone_id}>{z.nom}</option>)}
              </select>
              <div className="flex-1" />
              {!deplOpen && (
                <button type="button" onClick={() => openDepl(null, null)} data-testid="open-depl-btn"
                  className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg"
                  style={{ background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }}>
                  <Move size={11} /> Déplacer une zone
                </button>
              )}
              {!ajustOpen && (
                <button type="button" onClick={() => setAjustOpen(true)} data-testid="open-ajust-btn"
                  className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg"
                  style={{ background: '#F0FDF4', color: '#15803D', border: '1px solid #86EFAC' }}>
                  <TrendingUp size={11} /> Entrée de stock
                </button>
              )}
            </div>

            {/* Ajustement form inline */}
            {ajustOpen && (
              <div className="rounded-xl p-3 border space-y-3" style={{ borderColor: '#86EFAC', background: '#F0FDF4' }}>
                <p className="text-xs font-semibold" style={{ color: '#15803D' }}>Entrée / Sortie / Ajustement</p>
                <ArticleSearchField value={ajustForm.article_id} onChange={v => setAjustForm(f => ({ ...f, article_id: v, numero_serie: '' }))} articles={allArticles} />

                {/* Matériel notice */}
                {(() => {
                  const selectedArt = allArticles.find(a => a.article_id === ajustForm.article_id);
                  const isMaterielEntry = selectedArt?.type === 'materiel' && ajustForm.type_mouvement === 'entree';
                  return isMaterielEntry ? (
                    <div className="rounded-lg px-3 py-2 flex items-start gap-2"
                      style={{ background: '#EFF6FF', border: '1px solid #BFDBFE' }}>
                      <AlertCircle size={13} className="flex-shrink-0 mt-0.5" style={{ color: '#1D4ED8' }} />
                      <p className="text-xs" style={{ color: '#1D4ED8' }}>
                        Article de type <strong>matériel</strong> — quantité fixée à <strong>1 unité</strong>.
                        Une <strong>fiche matériel</strong> sera créée automatiquement avec le numéro de série.
                      </p>
                    </div>
                  ) : null;
                })()}

                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className={lbl} style={lblSt}>Type</label>
                    <select value={ajustForm.type_mouvement}
                      onChange={e => setAjustForm(f => ({ ...f, type_mouvement: e.target.value }))}
                      className={inp} style={inpSt}>
                      <option value="entree">Entrée</option>
                      <option value="sortie">Sortie</option>
                      <option value="ajustement">Ajustement</option>
                    </select>
                  </div>
                  <div>
                    <label className={lbl} style={lblSt}>Zone</label>
                    <select value={ajustForm.zone_id} onChange={e => setAjustForm(f => ({ ...f, zone_id: e.target.value }))}
                      className={inp} style={inpSt}>
                      <option value="">Sans zone</option>
                      {zones.map(z => <option key={z.zone_id} value={z.zone_id}>{z.nom}</option>)}
                    </select>
                  </div>
                  {(() => {
                    const selectedArt = allArticles.find(a => a.article_id === ajustForm.article_id);
                    const isMaterielEntry = selectedArt?.type === 'materiel' && ajustForm.type_mouvement === 'entree';
                    return isMaterielEntry ? (
                      <div className="flex items-end">
                        <div className="w-full px-2.5 py-2 rounded-lg text-xs font-semibold text-center"
                          style={{ background: '#BFDBFE', color: '#1D4ED8', border: '1px solid #93C5FD' }}>
                          Qté : 1 (fixe)
                        </div>
                      </div>
                    ) : (
                      <div>
                        <label className={lbl} style={lblSt}>Quantité</label>
                        <input type="number" min="0" step="any" value={ajustForm.quantite}
                          onChange={e => setAjustForm(f => ({ ...f, quantite: e.target.value }))}
                          className={inp} style={inpSt} placeholder="0" />
                      </div>
                    );
                  })()}
                </div>

                {/* Numéro de série — matériel uniquement */}
                {(() => {
                  const selectedArt = allArticles.find(a => a.article_id === ajustForm.article_id);
                  return selectedArt?.type === 'materiel' && ajustForm.type_mouvement === 'entree' ? (
                    <div>
                      <label className={lbl} style={{ color: '#DC2626' }}>
                        Numéro de série <span>*</span>
                      </label>
                      <input value={ajustForm.numero_serie}
                        onChange={e => setAjustForm(f => ({ ...f, numero_serie: e.target.value }))}
                        placeholder="Ex: SN-2024-00123" className={inp}
                        style={{ borderColor: ajustForm.numero_serie ? '#86EFAC' : '#FCA5A5' }}
                        data-testid="numero-serie-input" autoFocus />
                    </div>
                  ) : null;
                })()}

                {ajustForm.type_mouvement === 'entree' && (
                  <div>
                    <label className={lbl} style={lblSt}>Prix unitaire (optionnel)</label>
                    <PriceInput value={ajustForm.prix_unitaire}
                      onChange={v => setAjustForm(f => ({ ...f, prix_unitaire: v }))}
                      className={inp} style={inpSt} placeholder="0,00" />
                  </div>
                )}
                <div>
                  <label className={lbl} style={lblSt}>Commentaire</label>
                  <input value={ajustForm.commentaire} onChange={e => setAjustForm(f => ({ ...f, commentaire: e.target.value }))}
                    placeholder="Motif, référence BL…" className={inp} style={inpSt} />
                </div>
                <div className="flex gap-2 justify-end">
                  <button type="button" onClick={() => setAjustOpen(false)}
                    className="px-3 py-1.5 rounded-lg text-xs" style={{ color: 'var(--text-muted)' }}>Annuler</button>
                  <button type="button" onClick={handleAjuster} disabled={saving}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
                    style={{ background: 'var(--primary-gradient)' }}>
                    <Check size={11} /> Valider
                  </button>
                </div>
              </div>
            )}

            {/* Déplacement form inline */}
            {deplOpen && (
              <div className="rounded-xl p-3 border space-y-3" style={{ borderColor: '#BFDBFE', background: '#EFF6FF' }}>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold" style={{ color: '#1D4ED8' }}>Déplacer vers un autre stock / zone</p>
                  <div className="flex gap-1">
                    {['article', 'zone'].map(m => (
                      <button key={m} type="button" onClick={() => setDeplForm(f => ({ ...f, mode: m, article_id: '', zone_source_id: '' }))}
                        className="px-2.5 py-1 rounded-full text-xs font-medium border transition-all capitalize"
                        style={{
                          background: deplForm.mode === m ? '#1D4ED8' : 'white',
                          color: deplForm.mode === m ? 'white' : '#1D4ED8',
                          borderColor: '#BFDBFE',
                        }}>
                        {m === 'article' ? 'Cet article' : 'Toute une zone'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Source */}
                <div className="grid grid-cols-2 gap-2">
                  {deplForm.mode === 'article' ? (
                    <div>
                      <label className={lbl} style={lblSt}>Article source</label>
                      <ArticleSearchField value={deplForm.article_id}
                        onChange={v => {
                          const a = stockArticles.find(x => x.article_id === v);
                          setDeplForm(f => ({ ...f, article_id: v, zone_source_id: a?.zone_id || '', quantite: String(a?.quantite_disponible || '') }));
                        }}
                        articles={allArticles.filter(a => stockArticles.some(sa => sa.article_id === a.article_id && sa.quantite_disponible > 0))} />
                    </div>
                  ) : (
                    <div>
                      <label className={lbl} style={lblSt}>Zone source</label>
                      <select value={deplForm.zone_source_id}
                        onChange={e => setDeplForm(f => ({ ...f, zone_source_id: e.target.value }))}
                        className={inp} style={inpSt}>
                        <option value="">— Choisir —</option>
                        {zones.map(z => <option key={z.zone_id} value={z.zone_id}>{z.nom}</option>)}
                      </select>
                    </div>
                  )}
                  {deplForm.mode === 'article' && (
                    <div>
                      <label className={lbl} style={lblSt}>
                        Quantité
                        {deplForm.article_id && (() => {
                          const a = stockArticles.find(x => x.article_id === deplForm.article_id && (!deplForm.zone_source_id || x.zone_id === deplForm.zone_source_id));
                          return a ? (
                            <button type="button"
                              onClick={() => setDeplForm(f => ({ ...f, quantite: String(a.quantite_disponible) }))}
                              className="ml-2 px-1.5 py-0.5 rounded text-xs font-medium"
                              style={{ background: '#BFDBFE', color: '#1D4ED8' }}>
                              Tout ({a.quantite_disponible})
                            </button>
                          ) : null;
                        })()}
                      </label>
                      <input type="number" min="0" step="any" value={deplForm.quantite}
                        onChange={e => setDeplForm(f => ({ ...f, quantite: e.target.value }))}
                        className={inp} style={inpSt} placeholder="0" />
                    </div>
                  )}
                </div>

                {/* Destination */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className={lbl} style={lblSt}>Stock destination</label>
                    <select value={deplForm.stock_dest_id}
                      onChange={e => setDeplForm(f => ({ ...f, stock_dest_id: e.target.value, zone_dest_id: '' }))}
                      className={inp} style={inpSt}>
                      <option value="">— Choisir —</option>
                      {stocks.map(s => (
                        <option key={s.stock_id} value={s.stock_id}>
                          {s.nom}{s.agence_nom ? ` (${s.agence_nom})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={lbl} style={lblSt}>Zone destination</label>
                    <select value={deplForm.zone_dest_id}
                      onChange={e => setDeplForm(f => ({ ...f, zone_dest_id: e.target.value }))}
                      className={inp} style={inpSt} disabled={!deplForm.stock_dest_id}>
                      <option value="">Sans zone</option>
                      {destZones.map(z => <option key={z.zone_id} value={z.zone_id}>{z.nom}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className={lbl} style={lblSt}>Commentaire</label>
                  <input value={deplForm.commentaire} onChange={e => setDeplForm(f => ({ ...f, commentaire: e.target.value }))}
                    placeholder="Motif du déplacement…" className={inp} style={inpSt} />
                </div>
                <div className="flex gap-2 justify-end">
                  <button type="button" onClick={() => setDeplOpen(false)}
                    className="px-3 py-1.5 rounded-lg text-xs" style={{ color: 'var(--text-muted)' }}>Annuler</button>
                  <button type="button" onClick={handleDeplacer} disabled={saving}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
                    style={{ background: 'var(--primary-gradient)' }}>
                    <ArrowRight size={11} /> Déplacer
                  </button>
                </div>
              </div>
            )}

            {/* Articles table */}
            {filteredArticles.length === 0 ? (
              <div className="rounded-xl py-10 text-center" style={{ border: '1px dashed var(--border)' }}>
                <Package size={28} className="mx-auto mb-2" style={{ color: 'var(--text-disabled)' }} />
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Aucun article — faites une entrée de stock</p>
              </div>
            ) : (
              <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border-light)' }}>
                      {['Réf', 'Désignation', 'Zone', 'Commandé', 'Comptable', 'Physique', 'Réservé', 'Disponible', 'PAMP', ''].map((h, i) => (
                        <th key={i} className="px-2.5 py-2 text-left font-semibold uppercase tracking-wide"
                          style={{ color: 'var(--text-muted)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredArticles.map(a => {
                      const cmd  = Number(a.quantite_commandee || 0);
                      const cpt  = Number(a.quantite_comptable  || 0);
                      const phy  = Number(a.quantite_physique   || 0);
                      const res  = Number(a.quantite_reservee   || 0);
                      const dsp  = Number(a.quantite_disponible || 0);
                      const proj = phy + cmd + cpt - res;
                      return (
                        <tr key={a.entry_id} data-testid="stock-article-row"
                          style={{ borderBottom: '1px solid var(--border-light)' }}>
                          <td className="px-2.5 py-2">
                            <span className="font-mono px-1.5 py-0.5 rounded"
                              style={{ background: 'var(--border-light)', color: 'var(--text-secondary)' }}>
                              {a.article_ref || '—'}
                            </span>
                          </td>
                          <td className="px-2.5 py-2 font-medium" style={{ color: 'var(--text)', maxWidth: 160 }}>
                            <span className="truncate block">{a.article_nom || '—'}</span>
                          </td>
                          <td className="px-2.5 py-2">
                            {a.zone_id
                              ? <ZoneBadge type={zones.find(z => z.zone_id === a.zone_id)?.type_zone || 'autre'} />
                              : <span style={{ color: 'var(--text-disabled)' }}>—</span>}
                            {a.zone_id && <span className="ml-1 text-xs" style={{ color: 'var(--text-muted)' }}>{getZoneName(a.zone_id)}</span>}
                          </td>
                          {/* Dual-stock counters */}
                          <td className="px-2.5 py-2 text-right tabular-nums" style={{ color: cmd > 0 ? '#6366F1' : 'var(--text-disabled)' }}>{cmd > 0 ? cmd : '—'}</td>
                          <td className="px-2.5 py-2 text-right tabular-nums" style={{ color: cpt > 0 ? '#0369A1' : 'var(--text-disabled)' }}>{cpt > 0 ? cpt : '—'}</td>
                          <td className="px-2.5 py-2 text-right tabular-nums" style={{ color: phy > 0 ? '#047857' : 'var(--text-disabled)' }}>{phy > 0 ? phy : '—'}</td>
                          <td className="px-2.5 py-2 text-right tabular-nums" style={{ color: res > 0 ? '#B45309' : 'var(--text-disabled)' }}>{res > 0 ? res : '—'}</td>
                          <td className="px-2.5 py-2 text-right">
                            <span className="font-bold tabular-nums" title={`Projeté : ${proj}`} style={{
                              color: dsp <= 0 ? '#DC2626'
                                : (a.quantite_min != null && dsp <= a.quantite_min) ? '#B45309'
                                : '#15803D'
                            }}>
                              {formatPrice(dsp)}
                            </span>
                            {a.quantite_min != null && (
                              <span className="ml-1" style={{ color: 'var(--text-placeholder)' }}>(min {a.quantite_min})</span>
                            )}
                          </td>
                          <td className="px-2.5 py-2" style={{ color: 'var(--text-secondary)' }}>
                            {a.prix_moyen_pondere != null
                              ? <>{formatPrice(a.prix_moyen_pondere)} €</>
                              : <span style={{ color: 'var(--text-disabled)' }}>—</span>}
                          </td>
                          <td className="px-2.5 py-2">
                            <button type="button" title="Déplacer"
                              onClick={() => { openDepl(a, a.zone_id); setActiveTab('articles'); }}
                              className="p-1 rounded hover:bg-blue-50" style={{ color: '#1D4ED8' }}>
                              <Move size={12} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── Mouvements tab ── */}
        {activeTab === 'mouvements' && editStock && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                Derniers mouvements ({mouvements.length})
              </p>
              <button type="button" onClick={() => loadStockDetail(editStock.stock_id)}
                className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg"
                style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)' }}>
                <RefreshCw size={11} /> Actualiser
              </button>
            </div>

            {mouvements.length === 0 ? (
              <div className="rounded-xl py-10 text-center" style={{ border: '1px dashed var(--border)' }}>
                <History size={28} className="mx-auto mb-2" style={{ color: 'var(--text-disabled)' }} />
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Aucun mouvement enregistré</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {mouvements.map(m => (
                  <div key={m.mouvement_id} data-testid="mouvement-row"
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl border bg-white text-xs"
                    style={{ borderColor: 'var(--border)' }}>
                    {/* Type badge */}
                    <span className="px-2 py-0.5 rounded-full font-semibold flex-shrink-0"
                      style={{
                        background: TYPE_MVT_COLORS[m.type] + '18',
                        color: TYPE_MVT_COLORS[m.type]
                      }}>
                      {TYPE_MVT_LABELS[m.type] || m.type}
                    </span>
                    {/* Article */}
                    <div className="flex-1 min-w-0">
                      <span className="font-mono px-1 py-0.5 rounded mr-1"
                        style={{ background: 'var(--border-light)', color: 'var(--text-secondary)' }}>
                        {m.article_ref || '—'}
                      </span>
                      <span className="truncate" style={{ color: 'var(--text)' }}>{m.article_nom}</span>
                      {m.numero_serie && (
                        <span className="ml-1.5 px-1.5 py-0.5 rounded text-xs font-mono"
                          style={{ background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }}>
                          S/N {m.numero_serie}
                        </span>
                      )}
                    </div>
                    {/* Qty */}
                    <span className="font-bold flex-shrink-0"
                      style={{ color: m.type === 'entree' ? '#15803D' : m.type === 'sortie' ? '#DC2626' : '#1D4ED8' }}>
                      {m.type === 'entree' ? '+' : m.type === 'sortie' ? '-' : ''}
                      {formatPrice(m.quantite || 0)}
                    </span>
                    {/* Route */}
                    {m.type === 'deplacement' && (
                      <span className="flex items-center gap-1 text-xs flex-shrink-0" style={{ color: 'var(--text-muted)' }}>
                        {getStockName(m.stock_source_id)}
                        {m.zone_source_id && <span>/{getZoneName(m.zone_source_id)}</span>}
                        <ArrowRight size={10} />
                        {getStockName(m.stock_dest_id)}
                        {m.zone_dest_id && <span>/{getZoneName(m.zone_dest_id)}</span>}
                      </span>
                    )}
                    {/* User + date */}
                    <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-placeholder)' }}>
                      {m.user_nom} · {new Date(m.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                    </span>
                    {/* Comment */}
                    {m.commentaire && (
                      <span title={m.commentaire} className="flex-shrink-0" style={{ color: 'var(--text-placeholder)' }}>
                        <AlertCircle size={11} />
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {/* ── Inventaires tab ── */}
        {activeTab === 'inventaires' && editStock && (
          <StocksInventaireTab
            stockId={editStock.stock_id}
            stockNom={editStock.nom}
            zones={zones}
            stockArticles={stockArticles}
          />
        )}
      </SidePanel>
    </div>
  );
}
